import type { ZodError } from 'zod';

/** Request never reached the server (DNS, offline, CORS, abort). */
export class NetworkError extends Error {
  readonly kind = 'network';
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Server answered with a non-2xx status. */
export class HttpError extends Error {
  readonly kind = 'http';
  constructor(
    readonly status: number,
    readonly url: string,
    readonly body?: unknown,
  ) {
    super(`HTTP ${status} for ${url}`);
    this.name = 'HttpError';
  }
}

/**
 * Response arrived but does not match the contract schema. `zodError` is null
 * when the body was not JSON at all (nothing to run a schema against).
 */
export class ValidationError extends Error {
  readonly kind = 'validation';
  constructor(
    readonly url: string,
    readonly zodError: ZodError | null,
    message?: string,
  ) {
    super(message ?? `Response validation failed for ${url}`);
    this.name = 'ValidationError';
  }
}

export type ApiError = NetworkError | HttpError | ValidationError;

export const isApiError = (e: unknown): e is ApiError =>
  e instanceof NetworkError ||
  e instanceof HttpError ||
  e instanceof ValidationError;
